import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getTheme, type ThemeMode } from "../config/theme";

const STORAGE_KEY = "toronq-theme-mode";

interface ThemeContextValue {
	mode: ThemeMode;
	isDark: boolean;
	theme: ReturnType<typeof getTheme>["colors"];
	setMode: (mode: ThemeMode) => void;
	toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getInitialMode(): ThemeMode {
	if (typeof window === "undefined") return "light";

	const stored = window.localStorage.getItem(STORAGE_KEY);
	if (stored === "light" || stored === "dark") return stored;

	// Default to light mode unless the user explicitly picks dark mode.
	return "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
	const [mode, setMode] = useState<ThemeMode>(getInitialMode);

	// Note: we deliberately do NOT toggle a "dark" class on <html>/<body> here.
	// Theme is only ever applied inside the dashboard app shell (see
	// DashboardLayout.js), so public/marketing pages (Login, Register, etc.)
	// always render in light mode regardless of the stored preference.
	useEffect(() => {
		window.localStorage.setItem(STORAGE_KEY, mode);
	}, [mode]);

	const value = useMemo(
		() => ({
			mode,
			isDark: mode === "dark",
			theme: getTheme(mode).colors,
			setMode,
			toggleTheme: () =>
				setMode((prev) => (prev === "dark" ? "light" : "dark")),
		}),
		[mode],
	);

	return (
		<ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
	);
}

export function useTheme() {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
}
